import { useEffect, useState } from 'react';
import type { CasinoWarLegal, CasinoWarOutcome, CasinoWarView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, useNarrow } from './common';

export function CasinoWarTable() {
  const t = useT();
  const view = useGame((s) => s.view) as CasinoWarView;
  const legal = useGame((s) => s.legal) as CasinoWarLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const narrow = useNarrow();
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = Boolean(myTurn && legal?.kind === 'bet');
  const deciding = Boolean(myTurn && legal?.kind === 'tie');
  const [amount, setAmount] = useState<number>(view.config.minBet);
  const [tieOn, setTieOn] = useState(false);
  const ended = view.phase === 'HAND_END';
  const dealing = view.phase === 'DEALING';
  const war = view.phase === 'WAR';
  const myResult = view.lastResult?.seats.find((r) => r.seat === mySeat);
  const tieBet = view.config.minBet;

  useEffect(() => {
    if (legal?.kind === 'bet') setAmount((a) => Math.max(legal.min, Math.min(a, legal.max)));
  }, [legal, view.handNo]);

  const canTie = legal?.kind === 'bet' && legal.tieBetMax >= tieBet && amount + tieBet <= (me?.chips ?? 0);
  const quick = legal?.kind === 'bet' ? [...new Set([1, 2, 5, 10, 25].map((k) => legal.min * k).filter((v) => v <= legal.max).concat(legal.max))].sort((a, b) => a - b) : [];
  const card = (id: string | null | undefined) => (id ? <PlayingCard id={id} size="sm" /> : <span className="dim">–</span>);
  const outcomeText = (o: CasinoWarOutcome | null, delta: number) =>
    o === 'WIN' ? t('cw.outWin', { n: delta }) : o === 'WAR_WIN' ? t('cw.outWar', { n: delta }) : o === 'WAR_LOSE' ? t('cw.outWarLost', { n: -delta }) : o === 'SURRENDER' ? t('cw.outSurrender', { n: -delta }) : o === 'LOSE' ? t('ab.youLost', { n: -delta }) : '';

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer">
          <div className="card glass col" style={{ padding: 8, gap: 6, alignItems: 'center' }} aria-label={t('cw.dealer')}>
            <strong>{t('cw.dealer')}</strong>
            <div className="row" style={{ gap: 8, minHeight: narrow ? 52 : 68, alignItems: 'center' }}>
              {card(view.dealer)}
              {view.dealerWar && card(view.dealerWar)}
            </div>
            {war && <span className="center-note">{t('cw.warOn')}</span>}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 8, minHeight: 28, flexWrap: 'wrap' }}>
            {ended && myResult && (
              <span className={`pill ${myResult.delta > 0 ? 'accent' : myResult.delta < 0 ? 'danger' : ''}`} role="status">
                {myResult.delta === 0 ? t('bc.push') : outcomeText(myResult.outcome, myResult.delta)}
              </span>
            )}
            {dealing && <span className="center-note">{t('ab.dealing')}</span>}
            {view.phase === 'WAITING' && <span className="center-note">{t('bj.waiting')}</span>}
            {view.phase === 'BETTING' && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('table.waitingFor', { name: name(turn.seats[0]) })}</span>}
            {view.phase === 'DECIDING' && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('cw.waitTie', { name: name(turn.seats[0]) })}</span>}
          </div>
          <div className="card glass col" style={{ padding: 8, gap: 6 }} aria-label={t('cw.bets')}>
            {view.players.map((p) => {
              if (!p || p.status !== 'BET') return null;
              const res = view.lastResult?.seats.find((r) => r.seat === p.seat);
              return (
                <div key={p.seat} className="row" style={{ gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)', minWidth: 70 }}>{p.seat === mySeat ? t('cg.you') : name(p.seat)}</span>
                  <span className="pill" style={{ fontSize: 'var(--fs-xs)' }}>
                    {p.bet}
                    {p.raise > 0 ? ` + ${p.raise}` : ''}
                    {p.tieBet > 0 ? ` · ${t('cw.tieBet')} ${p.tieBet}` : ''}
                  </span>
                  <div className="row" style={{ gap: 4, alignItems: 'center' }}>
                    {card(p.card)}
                    {p.warCard && card(p.warCard)}
                  </div>
                  {ended && res && <span className={`pill ${res.delta > 0 ? 'accent' : res.delta < 0 ? 'danger' : ''}`} style={{ fontSize: 'var(--fs-xs)' }}>{res.delta > 0 ? '+' : ''}{res.delta}</span>}
                </div>
              );
            })}
          </div>
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('bj.placeBet')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('bj.placeBet')}>
              {quick.map((v) => (
                <button key={v} role="radio" aria-checked={amount === v} className={`bid-chip ${amount === v ? 'on' : ''}`} onClick={() => setAmount(v)}>
                  {v}
                </button>
              ))}
            </div>
            {canTie && (
              <label className="row" style={{ gap: 8, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
                <input type="checkbox" checked={tieOn} onChange={(e) => setTieOn(e.target.checked)} />
                {t('cw.addTieBet', { n: tieBet, p: view.config.tieBetPays })}
              </label>
            )}
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', amount, tieBet: canTie && tieOn ? tieBet : 0 })}>
                {t('cw.bet', { n: amount })}
              </button>
            </div>
          </div>
        )}
        {deciding && legal?.kind === 'tie' && me && (
          <div className="bid-picker" role="group" aria-label={t('cw.tied')}>
            <div className="bid-head">
              <strong>{t('cw.tied')}</strong>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'surrender' })}>
                {t('cw.surrender', { n: Math.floor(me.bet / 2) })}
              </button>
              <button className="btn primary grow" disabled={acting || !legal.canWar} onClick={() => void act({ type: 'war' })}>
                {t('cw.war', { n: legal.raise })}
              </button>
            </div>
          </div>
        )}
        {!betting && !deciding && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {me && me.status === 'BET' ? t('cw.yourBet', { n: me.bet }) : ended ? t('bj.handOver') : dealing || war ? t('ab.dealing') : view.phase === 'BETTING' ? t('bj.betting') : t('bj.waiting')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
